import { type ReactNode } from 'react';
import { type ZodType } from 'zod';

import { DEFAULT_MAX_IMPORT_FILE_SIZE } from './constants';
import { type DataImportCopyOverrides } from './dataImport.copy';
import {
  type DataImportParserRegistry,
  mergeDataImportParsers,
  type ParsedImportFile,
} from './dataImport.parsers';
import {
  type DataImportPreviewLocalIssue,
  type DataImportPreviewModel,
} from './dataImport.preview';

export type FormState = {
  data: Record<string, unknown>;
  errors: Record<string, string[]>;
  isValid: boolean;
};

export type FormStateHandler = (formState: FormState) => void;

export type RenderAdditionalForm = (
  updateForm: FormStateHandler,
  context: Record<string, unknown>,
) => ReactNode | undefined;

export type DataImportInstructions = {
  additionalInformationLink?: string;
  /** Single download offered beside the instructions; `content.templates` supersedes it. */
  templateFile?: File;
  text: string | string[];
};

export type DataImportTemplate = {
  file: File;
  /**
   * Identity of the template within its list, stable across the re-fetches
   * that replace `file`. Without one, a list keyed on filenames reorders
   * badly when two templates share a name.
   */
  id?: string;
  /** Replaces the generic "download template" wording, e.g. per file format. */
  label?: string;
};

export type DataImportFieldDefinition = {
  /**
   * Header the import file is expected to carry for this field. Matching it
   * pre-fills the column mapping; it is not what the user reads.
   */
  defaultSourceHeader?: string;
  field: string;
  /**
   * Marks a field whose values are dates. The preview renders their numeric
   * values (Excel serial numbers or timestamps) as localized dates instead
   * of the raw number.
   */
  isDate?: boolean;
  isMandatory?: boolean;
  /** Visible name of the field, independent of the header the file uses. */
  label?: string;
};

export type DataImportSource = {
  acceptedFileTypes: string[];
  /** Upper bound in bytes; {@link DEFAULT_MAX_IMPORT_FILE_SIZE} when omitted. */
  maxFileSize?: number;
  maxRows?: number;
  /**
   * Per-extension parsers layered over the built-in CSV and XLSX ones: an
   * entry replaces the default for its own extension and leaves the rest
   * intact, so registering one format never costs the others.
   */
  parsers?: DataImportParserRegistry;
};

export type DataImportFields = {
  definitions: readonly DataImportFieldDefinition[];
};

export type DataImportFileValidationInput = {
  columnMapping?: Record<string, string>;
  file: File;
  parsedFile: ParsedImportFile;
};

/** Whole-file checks a row schema cannot express, reported as preview issues. */
export type DataImportFileValidator = (
  input: DataImportFileValidationInput,
) =>
  | Promise<readonly DataImportPreviewLocalIssue[]>
  | readonly DataImportPreviewLocalIssue[];

export type DataImportConfirmContext<TPreview> = {
  model: DataImportPreviewModel;
  preview: TPreview;
};

export type DataImportConfirmDecision =
  | { allowed: true }
  | { allowed: false; reason?: string };

/**
 * Decides whether a previewed file may be imported. A policy replaces the
 * default severity rule instead of adding to it, so an import whose backend
 * reports error issues it can still accept is free to allow them. It never
 * widens what the wizard considers ready to submit: a policy only runs once a
 * complete preview and parsed data exist.
 */
export type DataImportConfirmPolicy<TPreview> = (
  context: DataImportConfirmContext<TPreview>,
) => DataImportConfirmDecision;

/**
 * The rule an import gets unless its definition replaces it: error issues
 * block confirmation, warnings only inform. It leaves the decision without a
 * reason, because the preview already lists the blocking issues.
 */
export function blockOnErrorIssues({
  model,
}: DataImportConfirmContext<unknown>): DataImportConfirmDecision {
  return model.issues.some((issue) => issue.severity === 'error')
    ? { allowed: false }
    : { allowed: true };
}

export type DataImportValidation<TSchema extends ZodType, TPreview> = {
  canConfirm?: DataImportConfirmPolicy<TPreview>;
  headerSchema?: ZodType;
  rowSchema: TSchema;
  validateFile?: DataImportFileValidator;
};

export type DataImportContent = {
  copy?: DataImportCopyOverrides;
  instructions: DataImportInstructions;
  readonly renderAdditionalForm?: RenderAdditionalForm;
  templates?: readonly DataImportTemplate[];
};

export type DataImportDefinition<
  TSchema extends ZodType = ZodType,
  TPreview = unknown,
> = {
  content: DataImportContent;
  fields?: DataImportFields;
  source: DataImportSource;
  validation: DataImportValidation<TSchema, TPreview>;
};

/**
 * @deprecated Flat authoring shape, accepted anywhere a
 * {@link DataImportDefinition} is. It cannot express per-import parsers, and a
 * field's visible label is whatever header its template uses, so the two can
 * never differ. Author imports as a definition.
 */
export type DataImportConfig<TValidationSchema extends ZodType = ZodType> = {
  file: {
    acceptedFileTypes: string[];
    allHeaderFields?: string[];
    /**
     * Expected-field names holding dates. The preview renders their numeric
     * values (Excel serial numbers or timestamps) as localized dates instead
     * of the raw number.
     */
    dateFields?: string[];
    defaultColumnMapping?: Record<string, string>;
    headerValidationRules?: ZodType;
    mandatoryHeaderFields?: string[];
    /** Upper bound in bytes; {@link DEFAULT_MAX_IMPORT_FILE_SIZE} when omitted. */
    maxFileSize?: number;
    validationRules: TValidationSchema;
  };
  instructions: DataImportInstructions;
  readonly renderAdditionalForm?: RenderAdditionalForm;
  title?: string;
};

export type DataImportConfigInput<
  TSchema extends ZodType = ZodType,
  TPreview = unknown,
> = DataImportConfig<TSchema> | DataImportDefinition<TSchema, TPreview>;

export type ResolvedDataImportFile<TSchema extends ZodType = ZodType> = {
  acceptedFileTypes: string[];
  allHeaderFields?: string[];
  dateFields?: string[];
  defaultColumnMapping?: Record<string, string>;
  /** Visible label per expected field, keyed by field name. */
  fieldLabels?: Record<string, string>;
  headerValidationRules?: ZodType;
  mandatoryHeaderFields?: string[];
  /** Always populated: the config's own bound or the shared default. */
  maxFileSize: number;
  maxRows?: number;
  /**
   * The effective registry, any overrides already merged over the defaults.
   * Always populated, so a consumer hands it straight to `parseImportFile`
   * instead of carrying a second fallback to the defaults.
   */
  parsers: DataImportParserRegistry;
  validationRules: TSchema;
};

/**
 * The single shape the components and the wizard hook consume, so neither has
 * to know which authoring shape it came from.
 */
export type ResolvedDataImportConfig<
  TSchema extends ZodType = ZodType,
  TPreview = unknown,
> = {
  canConfirm?: DataImportConfirmPolicy<TPreview>;
  copy?: DataImportCopyOverrides;
  fields: readonly DataImportFieldDefinition[];
  file: ResolvedDataImportFile<TSchema>;
  instructions: DataImportInstructions;
  renderAdditionalForm?: RenderAdditionalForm;
  templates: readonly DataImportTemplate[];
  /** Only a legacy config carries one; a definition leaves the heading to its host. */
  title?: string;
  validateFile?: DataImportFileValidator;
};

function pickFields(
  definitions: readonly DataImportFieldDefinition[],
  predicate: (definition: DataImportFieldDefinition) => boolean | undefined,
): string[] | undefined {
  const fields = definitions.flatMap((definition) =>
    predicate(definition) ? [definition.field] : [],
  );

  return fields.length > 0 ? fields : undefined;
}

function toFieldRecord(
  definitions: readonly DataImportFieldDefinition[],
  select: (definition: DataImportFieldDefinition) => string | undefined,
): Record<string, string> | undefined {
  const entries = definitions.flatMap((definition) => {
    const value = select(definition);

    return value === undefined ? [] : [[definition.field, value] as const];
  });

  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/**
 * The one place the two ways of authoring a download are reconciled: an
 * explicit list wins, and a legacy single `templateFile` is adapted into the
 * same collection, so everything downstream sees only a list.
 */
function resolveTemplates(
  instructions: DataImportInstructions,
  templates?: readonly DataImportTemplate[],
): readonly DataImportTemplate[] {
  if (templates && templates.length > 0) return templates;

  return instructions.templateFile ? [{ file: instructions.templateFile }] : [];
}

/**
 * A legacy config conflates the visible field name with the header its
 * template uses, so both derive from `defaultColumnMapping`.
 */
function toLegacyFieldDefinitions(
  file: DataImportConfig['file'],
): DataImportFieldDefinition[] {
  const dateFields = new Set(file.dateFields);
  const mandatoryFields = new Set(file.mandatoryHeaderFields);

  return (file.allHeaderFields ?? []).map((field) => {
    const defaultSourceHeader = file.defaultColumnMapping?.[field];

    return {
      defaultSourceHeader,
      field,
      isDate: dateFields.has(field),
      isMandatory: mandatoryFields.has(field),
      label: defaultSourceHeader,
    };
  });
}

function resolveLegacyConfig<TSchema extends ZodType, TPreview>(
  config: DataImportConfig<TSchema>,
): ResolvedDataImportConfig<TSchema, TPreview> {
  const { file, instructions, renderAdditionalForm, title } = config;

  return {
    fields: toLegacyFieldDefinitions(file),
    file: {
      acceptedFileTypes: file.acceptedFileTypes,
      allHeaderFields: file.allHeaderFields,
      dateFields: file.dateFields,
      defaultColumnMapping: file.defaultColumnMapping,
      fieldLabels: file.defaultColumnMapping,
      headerValidationRules: file.headerValidationRules,
      mandatoryHeaderFields: file.mandatoryHeaderFields,
      maxFileSize: file.maxFileSize ?? DEFAULT_MAX_IMPORT_FILE_SIZE,
      parsers: mergeDataImportParsers(),
      validationRules: file.validationRules,
    },
    instructions,
    renderAdditionalForm,
    templates: resolveTemplates(instructions),
    title,
  };
}

function resolveDefinition<TSchema extends ZodType, TPreview>(
  definition: DataImportDefinition<TSchema, TPreview>,
): ResolvedDataImportConfig<TSchema, TPreview> {
  const { content, fields, source, validation } = definition;
  const definitions = fields?.definitions ?? [];

  return {
    canConfirm: validation.canConfirm,
    copy: content.copy,
    fields: definitions,
    file: {
      acceptedFileTypes: source.acceptedFileTypes,
      // "No expected fields declared" is `undefined` to the mapping form and
      // the preview, so an empty list has to reach them the same way an
      // omitted `fields` slice does rather than as [].
      allHeaderFields: pickFields(definitions, () => true),
      dateFields: pickFields(definitions, ({ isDate }) => isDate),
      defaultColumnMapping: toFieldRecord(
        definitions,
        ({ defaultSourceHeader }) => defaultSourceHeader,
      ),
      fieldLabels: toFieldRecord(definitions, ({ label }) => label),
      headerValidationRules: validation.headerSchema,
      mandatoryHeaderFields: pickFields(
        definitions,
        ({ isMandatory }) => isMandatory,
      ),
      maxFileSize: source.maxFileSize ?? DEFAULT_MAX_IMPORT_FILE_SIZE,
      maxRows: source.maxRows,
      parsers: mergeDataImportParsers(source.parsers),
      validationRules: validation.rowSchema,
    },
    instructions: content.instructions,
    renderAdditionalForm: content.renderAdditionalForm,
    templates: resolveTemplates(content.instructions, content.templates),
    validateFile: validation.validateFile,
  };
}

export function resolveDataImportConfig<TSchema extends ZodType, TPreview>(
  config: DataImportConfigInput<TSchema, TPreview>,
): ResolvedDataImportConfig<TSchema, TPreview> {
  return 'source' in config
    ? resolveDefinition(config)
    : resolveLegacyConfig(config);
}
